import React, { useEffect, useState } from 'react';
import ActivateChatHistoryButton from './ActivateChatHistoryButton';
import MeetingRoomMembersView from './MeetingRoomMembersView';
import useMeetingMembers from './MeetingParticipantsList/useMeetingMembers';

import {
    clearSelectedThreadMessage,
    fetchChatSession,
} from 'flow/actions/chat-actions';
import { setActiveMeetingChatOpenStatusAction } from 'flow/actions/meeting-actions';
import IconButton from 'flow/components/ui-components/IconButton';
import Icon from 'flow/components/ui-components/Icon';
import Loading from 'flow/components/ui-components/Loading';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectShouldShowActivateChatHistoryButton } from 'flow/selectors/meeting-selectors';
import ChatBanner from 'flow/pages/messages/ChatBanner';
import MessageInput from 'flow/pages/messages/MessageInput';
import MessageList from 'flow/components/chat/v2/MessageList';
import NoChatProfileBanner from 'flow/pages/messages/NoChatProfileBanner';
import MessagesPage from 'flow/pages/MessagesPage';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { isDirectMessageSession } from 'flow/utils/message-utils-TW';

interface Props {
    chatSessionKey: ChatSessionKey;
}

interface MembersPageProps {
    chatSessionKey: ChatSessionKey;
    onClickBack: () => void;
}

/**
 * Split out so useMeetingMembers() — which subscribes to video-member state
 * and carries its own useState/useEffect accumulator — only runs while this
 * sub-view is actually mounted, instead of on every VideoChatSession render
 * (previously: every mic toggle / speaking change re-rendered the chat view
 * too, even though it never read the result).
 */
const MeetingRoomMembersPage = ({
    chatSessionKey,
    onClickBack,
}: MembersPageProps) => {
    const { i18next } = useAppContext();
    const { roomMembers, guestMembers } = useMeetingMembers();
    // Room-member count, not the live-participant count: this view (unlike
    // the video call's own participants panel) splits room members from
    // guests below a divider, so the header should match what it's a header
    // for — otherwise "Members (4)" over a list showing 1 member + 3 guests
    // reads as a contradiction.
    const roomMemberCount = roomMembers.length;

    return (
        <MessagesPage
            header={`${i18next.t('meetings.participants.members_header')}${
                roomMemberCount > 0 ? ` (${roomMemberCount})` : ''
            }`}
            onClickBack={onClickBack}
            chatSessionKey={chatSessionKey}
            videoDark={true}
        >
            <MeetingRoomMembersView
                roomMembers={roomMembers}
                guestMembers={guestMembers}
                chatSessionKey={chatSessionKey}
            />
        </MessagesPage>
    );
};

const VideoChatSession: React.FC<Props> = ({ chatSessionKey }) => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();

    // Toggled by the members icon in the chat header — a sub-view within
    // this same panel slot, distinct from the video call's own
    // MeetingParticipantsPanel (opened from the top toolbar).
    const [view, setView] = useState<'chat' | 'members'>('chat');

    const showActivateChatHistoryButton = useAppSelector(
        selectShouldShowActivateChatHistoryButton,
    );

    const session = useAppSelector((state) =>
        state.chats.getIn(['sessions', chatSessionKey]),
    );

    useEffect(() => {
        dispatch(fetchChatSession(chatSessionKey, 'contact'));
        dispatch(clearSelectedThreadMessage());
        setView('chat');
    }, [chatSessionKey, dispatch]);

    if (!session) {
        return <Loading />;
    }

    if (view === 'members') {
        return (
            <MeetingRoomMembersPage
                chatSessionKey={chatSessionKey}
                onClickBack={() => setView('chat')}
            />
        );
    }

    return (
        <MessagesPage
            header={session.get('description') || ''}
            headerAlign="start"
            endButton={
                <>
                    <IconButton
                        tooltipLabel={i18next.t(
                            'call.canvas.chat.view_members',
                        )}
                        onClick={() => setView('members')}
                        className="text-white"
                    >
                        <Icon iconKey="group" />
                    </IconButton>
                    <IconButton
                        onClick={() =>
                            dispatch(
                                setActiveMeetingChatOpenStatusAction(false),
                            )
                        }
                        aria-label={i18next.t(
                            'videoconference.controlpanel.close',
                        )}
                        className="text-white"
                    >
                        <Icon iconKey="close" />
                    </IconButton>
                </>
            }
            footer={
                <MessageInput chatSession={chatSessionKey} videoDark={true} />
            }
            chatSessionKey={chatSessionKey}
            videoDark={true}
        >
            {isDirectMessageSession(session) && (
                <div className="relative">
                    <div className="absolute top-6 z-30 flex w-full flex-col gap-2">
                        <NoChatProfileBanner session={session} />
                        <ChatBanner session={session} />
                    </div>
                </div>
            )}
            <div className="m-2 flex flex-col gap-2 rounded-sm bg-gray-700 px-3 py-2 text-sm text-gray-200">
                <span>
                    {i18next.t('call.canvas.chat.guest_notice')}
                    {showActivateChatHistoryButton &&
                        ` ${i18next.t('call.canvas.chat.activate_chat_history_prompt')}`}
                </span>
                <ActivateChatHistoryButton
                    meetingName={session.get('description') || ''}
                    videoDark={true}
                />
            </div>
            <MessageList chatSessionKey={chatSessionKey} videoDark={true} />
        </MessagesPage>
    );
};

export default VideoChatSession;
